// index.js
// Thin wrapper around <GuidePreviewFrame /> that builds the right
// URL into the published guest-guide app based on the selected
// rental and the active designer section.
import React, { forwardRef, useEffect, useState } from "react";
import GuidePreviewFrame from "./frame";
import SettingsService from "../../../services/settings.service";

export const resolveGuideBase = ({ configuredUrl, hostname } = {}) => {
  const explicitUrl = String(configuredUrl || "").trim();
  if (explicitUrl) {
    return explicitUrl.replace(/\/+$/, "");
  }

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8080";
  }

  return "https://guide.ruebarue.com";
};

const GUIDE_BASE = resolveGuideBase({
  configuredUrl: process.env.REACT_APP_GUIDE_URL,
  hostname: window.location.hostname,
});

const PREVIEW_TAB_PATHS = {
  area: "area-guide",
  extras: "extras",
  home: "your-stay",
};

export const getGuidePreviewTabPath = (activeDesigner) =>
  PREVIEW_TAB_PATHS[activeDesigner] || PREVIEW_TAB_PATHS.home;

const BuilderGuidePreview = forwardRef(
  ({ rental, activeDesigner }, ref) => {
    const [url, setUrl] = useState("");
    const [previewError, setPreviewError] = useState("");

    useEffect(() => {
      let cancelled = false;
      let refreshTimer;
      let hasToken = false;
      setUrl("");
      setPreviewError("");
      if (!rental?.rental_id) return undefined;

      const refreshSecureUrl = () => {
        SettingsService.createGuestbookPreviewToken(rental.rental_id)
          .then(({ data }) => {
            if (cancelled) return;
            hasToken = true;
            const tab = getGuidePreviewTabPath(activeDesigner);
            setUrl(
              `${GUIDE_BASE}/guestbook-preview/${rental.rental_id}/${tab}` +
                `?preview_token=${encodeURIComponent(data.token)}`,
            );
            setPreviewError("");
            refreshTimer = window.setTimeout(refreshSecureUrl, 9 * 60 * 1000);
          })
          .catch(() => {
            if (cancelled) return;
            if (!hasToken) {
              setPreviewError("Unable to create a secure preview. Try again.");
            }
            refreshTimer = window.setTimeout(refreshSecureUrl, 30 * 1000);
          });
      };

      refreshSecureUrl();

      return () => {
        cancelled = true;
        window.clearTimeout(refreshTimer);
      };
    }, [rental?.rental_id, activeDesigner]);

    const emptyMessage =
      previewError ||
      (!rental
        ? "Select a property to preview the guide."
        : "Creating secure preview…");

    return (
      <GuidePreviewFrame
        ref={ref}
        src={url}
        emptyMessage={emptyMessage}
      />
    );
  },
);

export default BuilderGuidePreview;
